import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useIsMobile } from "@/hooks/use-mobile";

const SectionTransition = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const isMobile = useIsMobile();
  
  // Hooks must be called unconditionally in React
  // useScroll can handle a ref that points to null (it just won't track)
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  // Particle stream flowing upward
  const particleY = useTransform(scrollYProgress, [0, 1], [200, -200]);
  
  // On mobile, return null (but hooks have already been called)
  if (isMobile) {
    return null;
  }

  return (
    <section 
      ref={sectionRef} 
      className="relative h-[15vh] -my-24 overflow-hidden"
    >
      {/* Particle stream flowing through transition - Disabled on mobile */}
      <div className="absolute inset-0 pointer-events-none">
        {Array.from({ length: 20 }).map((_, i) => {
          const randomX = Math.random() * 100;
          const randomDelay = Math.random() * 4;
          const randomDuration = 4 + Math.random() * 3;
          const isAqua = i % 2 === 0;
          const size = Math.random() * 2 + 1;
          
          return (
            <motion.div
              key={i}
              className="absolute rounded-full"
              style={{
                left: `${randomX}%`,
                top: '50%',
                width: `${size}px`,
                height: `${size}px`,
                background: isAqua 
                  ? "hsl(var(--aqua))" 
                  : "hsl(var(--pink))",
                boxShadow: isAqua
                  ? "0 0 8px hsl(var(--aqua) / 0.6), 0 0 16px hsl(var(--aqua) / 0.3)"
                  : "0 0 8px hsl(var(--pink) / 0.6), 0 0 16px hsl(var(--pink) / 0.3)",
                y: particleY,
              }}
              animate={{
                y: [-120, 120],
                opacity: [0, 0.8, 0.8, 0],
                scale: [0, 1.2, 1, 0],
              }}
              transition={{
                duration: randomDuration,
                repeat: Infinity,
                delay: randomDelay,
                ease: "easeInOut",
              }}
            />
          );
        })}
      </div>

      {/* Subtle flowing light beams - very low opacity */}
      {Array.from({ length: 6 }).map((_, i) => (
        <motion.div
          key={`beam-${i}`}
          className="absolute top-0 bottom-0"
          style={{
            left: `${15 + i * 14}%`,
            width: '1px',
            background: `linear-gradient(180deg, 
              transparent 0%, 
              hsl(var(--${i % 2 === 0 ? 'aqua' : 'pink'}) / 0.15) 50%, 
              transparent 100%
            )`,
          }}
          animate={{
            opacity: [0, 0.4, 0],
            scaleY: [0.5, 1, 0.5],
          }}
          transition={{
            duration: 3.5,
            repeat: Infinity,
            delay: i * 0.5,
            ease: "easeInOut",
          }}
        />
      ))}

      {/* Subtle radial glow pulses - very low opacity */}
      <motion.div
        className="absolute left-1/3 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{
          background: "radial-gradient(circle, hsl(var(--aqua) / 0.08) 0%, transparent 60%)",
        }}
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.5, 0.8, 0.5],
        }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />
      
      <motion.div
        className="absolute right-1/3 top-1/2 translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{
          background: "radial-gradient(circle, hsl(var(--pink) / 0.08) 0%, transparent 60%)",
        }}
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.5, 0.8, 0.5],
        }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 2.5,
        }}
      />

    </section>
  );
};

export default SectionTransition;
